import {
  getCourse,
  getCourses,
  getCoursesByInterest,
  getCourseFeeLine,
  getDurationAdvisorLines,
  getFacilitiesAdvisorLines,
  getHumanAdvisorLines,
  getIntakeAdvisorLines,
  getFoundingYearAdvisorLines,
  getHoursAdvisorLines,
  getLocationAdvisorLines,
  getPaymentAdvisorLines,
  getCertificateAdvisorLines,
  getEquipmentAdvisorLines,
  getVisitAdvisorLines,
  getRequirementsAdvisorLines,
  getMasterclassAdvisorLines,
  findToolAdvisorLines,
  getOfficialEmail,
  getOfficialSlogan,
  getOfficialWebsite,
  getUnknownAdvisorLines,
  getUpcomingMasterclass,
  formatMasterclassDate,
  findCourseForQuestion,
} from '../../knowledge'
import { askAdvisorAi, mapAdvisorActions, requestCourseRecommendation } from '../../services/advisorAiService'
import { appendConversationToRegisterHref } from '../../services/advisorLeadService'
import {
  emptyLead,
  extractName,
  extractPhone,
  firstName,
  getOrCreateConversationId,
  interestLabel,
  isContactIntent,
  isHumanIntent,
  isCtaDecline,
  shouldFollowCaptureStep,
  shouldOfferCapture,
  syncLead,
  chatAnswersFromState,
} from './leadCapture'

const PRIVACY_COPY = 'Your contact details will be used by SMA Admissions to follow up about your enquiry.'
const NAME_PROMPT = 'What name should SMA Admissions use?'
const PHONE_PROMPT = 'What WhatsApp number should we use to reach you?'
const HUMAN_SAFE_COPY = 'You can reach SMA Admissions through the contact details here.'
const CTA_DECLINE_COPY = "That's okay. Let me know if you'd like more information."

export const TOPICS = [
  { id: 'discover', label: 'Find the right course', support: 'Help me choose the best path.', userText: 'Help me choose the best path.' },
  { id: 'fees', label: 'Courses & fees', support: 'Show me programs and current fees.', userText: 'Show me programs and current fees.' },
  { id: 'intakes', label: 'Intakes & schedules', support: 'Tell me when and how I can study.', userText: 'Tell me when and how I can study.' },
  { id: 'learn', label: 'What will I learn?', support: 'Explore what each program teaches.', userText: 'Explore what each program teaches.' },
  { id: 'facilities', label: 'Equipment & facilities', support: 'See what I will have access to.', userText: 'See what I will have access to.' },
  { id: 'masterclass', label: 'Masterclass', support: 'Explore upcoming Masterclass sessions.', userText: 'Explore upcoming Masterclass sessions.' },
  { id: 'human', label: 'Talk to a human', support: 'Connect me with SMA Admissions.', userText: 'Connect me with SMA Admissions.' },
  { id: 'other', label: 'Ask anything', support: 'I have a specific question.', userText: 'I have a specific question.' },
]

export const INTERESTS = [
  { id: 'photos', label: 'Photography' },
  { id: 'videos', label: 'Videos' },
  { id: 'designs', label: 'Graphic designs' },
  { id: 'animations', label: 'Animations' },
  { id: 'ai', label: 'AI content' },
  { id: 'web', label: 'Websites & digital experiences' },
  { id: 'unsure', label: "I'm not sure yet" },
]

export const GOALS = [
  { id: 'career', label: 'Start a career' },
  { id: 'business', label: 'Build a business' },
  { id: 'skills', label: 'Improve my skills' },
  { id: 'content', label: 'Create content' },
  { id: 'freelance', label: 'Become a freelancer' },
  { id: 'explore', label: 'Explore a new skill' },
]

export const EXPERIENCE = [
  { id: 'beginner', label: 'Complete beginner' },
  { id: 'some', label: 'Some experience' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'professional', label: 'Professional' },
]

const AREAS = [
  { id: 'photos', label: 'Photography' },
  { id: 'videos', label: 'Film & video' },
  { id: 'designs', label: 'Graphic design' },
  { id: 'animations', label: 'Animation' },
  { id: 'ai', label: 'AI tools' },
  { id: 'web', label: 'UI/UX & digital' },
  { id: 'unsure', label: 'Show me a few paths' },
]

const FOLLOW_UP = [
  { id: 'discover', label: 'Find the right course', support: 'Help me choose the best path.' },
  { id: 'human', label: 'Talk to a human', support: 'Connect me with SMA Admissions.' },
  { id: 'other', label: 'Ask anything', support: 'I have a specific question.' },
]

function byCode(code) {
  return getCourse(code)
}

function coursesInArea(areaId) {
  return getCoursesByInterest(areaId)
}

export function whyItFits(course, answers, reason) {
  const code = reason?.reasonCode || reason?.code
  const title = course.title
  if (code === 'BEGINNER_PHOTOGRAPHY_FOUNDATION') {
    return `Because you're starting out and want photography as a career, ${title} is the best place to build your foundation.`
  }
  if (code === 'PHOTOGRAPHY_CAREER') {
    return `${title} fits because you want to build photography as a career.`
  }
  if (code === 'STUDIO_SPECIALIZATION') {
    return `${title} fits because you already have photography experience and want to specialize in studio or commercial work.`
  }
  if (code === 'PHOTO_EDITING_FOCUS') {
    return `${title} fits because your focus is professional photo editing and post-production.`
  }
  if (code === 'GRAPHIC_DESIGN_FOUNDATION') {
    return `${title} is SMA's foundation graphic design course, so it is the right place to start.`
  }
  if (code === 'BRAND_IDENTITY_SPECIALIZATION') {
    return `${title} fits because you want to specialize in brand identity and corporate design.`
  }
  if (code === 'CONTENT_CREATION_PATH') {
    return `${title} fits because you want to create digital content and grow on social media.`
  }
  if (code === 'VIDEO_EDITING_FOCUS') {
    return `${title} fits because your focus is video editing and post-production.`
  }
  if (code === 'VFX_SPECIALIZATION') {
    return `${title} fits because you want to specialize in visual effects.`
  }
  if (code === '3D_ANIMATION_SPECIALIZATION') {
    return `${title} fits because you want to specialize in 3D animation.`
  }
  if (code === 'CINEMATOGRAPHY_SPECIALIZATION') {
    return `${title} fits because you want to specialize in cinematography and film production.`
  }
  if (code === 'DRONE_SPECIALIZATION') {
    return `${title} fits because you want to learn drone photography and aerial videography.`
  }
  if (code === 'EXPLICIT_COURSE_SELECTION') {
    return `${title} is the course you asked for, so we can stay with this path.`
  }
  if (code === 'EXISTING_SKILL_PROGRESSION') {
    return `${title} fits as the next step from the experience you already have.`
  }

  const data = reason?.reasonData || reason?.data || {}
  const interest = INTERESTS.find((item) => item.id === answers.interest)?.label
    || (data.interest ? String(data.interest).replace(/_/g, ' ') : null)
    || 'this path'
  const goal = GOALS.find((item) => item.id === answers.goal)?.label
    || (data.goal ? String(data.goal).replace(/_/g, ' ') : null)
    || 'your goal'
  const experience = EXPERIENCE.find((item) => item.id === answers.experience)?.label
    || (data.experience && data.experience !== 'UNKNOWN' ? String(data.experience).replace(/_/g, ' ') : null)
    || 'your level'

  const duration = course.durationSource === 'postgresql' && course.duration
    ? `${course.duration.toLowerCase()} `
    : ''
  const level = (course.level || 'SMA').toLowerCase()
  const category = (course.category || 'creative').toLowerCase()

  return `${course.title} is SMA's ${duration}${level} ${category} program. It fits because you want to create ${interest.toLowerCase()}, your goal is to ${goal.toLowerCase()}, and you described yourself as a ${experience.toLowerCase()}.`
}

export function nextStepFits(nextStep) {
  if (!nextStep?.title) return null
  if (nextStep.relationship === 'COMPLEMENTARY') {
    return `If you also want to become stronger in that area, ${nextStep.title} would complement this.`
  }
  return `Once you're comfortable here, ${nextStep.title} is a strong next skill to add.`
}

function topicById(id) {
  return TOPICS.find((topic) => topic.id === id)
}

export function getCourseSpecificOptions(course) {
  if (!course) return TOPICS
  return [
    { id: `course_learn:${course.code}`, label: 'What will I learn?', support: 'Syllabus, modules & skills taught.', userText: `What will I learn in ${course.title}?` },
    { id: `course_fee:${course.code}`, label: 'Tuition & payment plans', support: `${course.fee} & installment options.`, userText: `How much is tuition for ${course.title} and what are the payment plans?` },
    { id: `course_duration:${course.code}`, label: 'Duration & schedules', support: `${course.duration} & class timings.`, userText: `How long is ${course.title} and what are the class schedules?` },
    { id: `course_careers:${course.code}`, label: 'Career pathways', support: 'Target job roles & portfolio projects.', userText: `What career opportunities follow ${course.title}?` },
    { id: `course_equipment:${course.code}`, label: 'Equipment & studios', support: 'Cameras, labs & software included.', userText: `What equipment and software are provided for ${course.title}?` },
    { id: `course_apply:${course.code}`, label: 'Apply for this course', href: `/register?course=${course.code}`, actionType: 'START_APPLICATION', support: 'Begin direct application.' },
    { id: 'discover', label: 'Explore other courses', support: 'Help me choose a different path.', userText: 'Help me explore other courses at SMA.' },
    { id: 'human', label: 'Talk to a human', support: 'Connect me with SMA Admissions.', userText: 'Connect me with SMA Admissions.' },
  ]
}

export function createCourseFollowUpOptions(course) {
  if (!course) return FOLLOW_UP
  return [
    { id: `course_learn:${course.code}`, label: 'What will I learn?', support: 'Syllabus & modules.' },
    { id: `course_fee:${course.code}`, label: 'Tuition & payment plans', support: `${course.fee} & installments.` },
    { id: `course_duration:${course.code}`, label: 'Duration & schedule', support: `${course.duration} & timings.` },
    { id: `course_careers:${course.code}`, label: 'Career pathways', support: 'Job roles & portfolio.' },
    { id: `course_equipment:${course.code}`, label: 'Equipment & studios', support: 'Gear & software.' },
    { id: `course_apply:${course.code}`, label: 'Apply for this course', href: `/register?course=${course.code}`, actionType: 'START_APPLICATION' },
    { id: 'human', label: 'Talk to a human', support: 'Connect with Admissions.' },
    { id: 'discover', label: 'Explore other courses', support: 'View all SMA courses.' },
  ]
}

export function createInitialState(previous, initialCourseCode) {
  const targetCode = initialCourseCode || previous?.answers?.targetCourseCode || previous?.recommendation?.code || null
  const targetCourse = targetCode ? byCode(targetCode) : null

  if (targetCourse) {
    return {
      messages: [
        { id: 'open-1', role: 'advisor', text: 'Hi! Welcome to Synergy Multimedia Academy.' },
        { id: 'open-2', role: 'advisor', text: `I see you are inquiring about ${targetCourse.title} (${targetCourse.code}).` },
        { id: 'open-3', role: 'advisor', text: `I'm here to answer your specific questions about this course — including curriculum modules, tuition fees & installment plans, schedules, studio equipment, and career pathways.` },
        { id: 'open-4', role: 'advisor', text: `What would you like to know about ${targetCourse.title}?` },
      ],
      step: 'course_inquiry',
      targetCourse,
      answers: previous?.answers ? {
        ...previous.answers,
        targetCourseCode: targetCourse.code,
        recommendedCourseCode: targetCourse.code,
      } : {
        targetCourseCode: targetCourse.code,
        recommendedCourseCode: targetCourse.code,
      },
      options: getCourseSpecificOptions(targetCourse),
      recommendation: targetCourse,
      inputMode: true,
      nextId: 5,
      conversationId: previous?.conversationId || getOrCreateConversationId(),
      lead: previous?.lead || emptyLead(),
    }
  }

  return {
    messages: [
      { id: 'open-1', role: 'advisor', text: 'Hi! Welcome to Synergy Multimedia Academy.' },
      { id: 'open-2', role: 'advisor', text: "I'm here to help you find the right course, understand how training works, and answer any questions you have." },
      { id: 'open-3', role: 'advisor', text: 'What can I help you with?' },
    ],
    step: 'topic',
    answers: previous?.answers ? {
      recommendedCourseCode: previous.answers.recommendedCourseCode || null,
      interest: previous.answers.interest || null,
      experience: previous.answers.experience || null,
      goal: previous.answers.goal || null,
      leadName: previous.answers.leadName || null,
      leadPhone: previous.answers.leadPhone || null,
    } : {},
    options: TOPICS,
    recommendation: previous?.recommendation || null,
    inputMode: false,
    nextId: 4,
    conversationId: previous?.conversationId || getOrCreateConversationId(),
    lead: previous?.lead || emptyLead(),
  }
}

function withMessages(state, entries, extras = {}) {
  let nextId = state.nextId
  const messages = [...state.messages]
  entries.forEach((entry) => {
    messages.push({ id: `m-${nextId}`, role: entry.role, text: entry.text })
    nextId += 1
  })
  return {
    ...state,
    ...extras,
    messages,
    nextId,
  }
}

function userThenAdvisor(state, userText, advisorTexts, extras = {}) {
  return withMessages(
    state,
    [{ role: 'visitor', text: userText }, ...advisorTexts.map((text) => ({ role: 'advisor', text }))],
    extras
  )
}

function visitorTurnCount(state) {
  return (state.messages || []).filter((item) => item.role === 'visitor').length
}

function canInviteCapture(state) {
  if (state.lead?.captured) return false
  if (state.answers?.contactDeclined) return false
  if (state.answers?.leadName && state.answers?.leadPhone) return false
  return visitorTurnCount(state) + 1 >= 3
}

function withCaptureInvite(state, advisorTexts, options = []) {
  if (!canInviteCapture(state)) {
    return { texts: advisorTexts, options }
  }
  return {
    texts: [
      ...advisorTexts,
      'Would you like me to save these details and have SMA Admissions follow up with you?',
    ],
    options: [
      { id: 'lead-yes', label: 'Yes, save my details', support: 'SMA Admissions can follow up.' },
      { id: 'lead-later', label: 'Not right now' },
      ...options,
    ],
  }
}

export function startAgain(state) {
  return createInitialState(state)
}

function registerOptions(state) {
  const courseCode = state.answers.recommendedCourseCode || state.recommendation?.code || null
  return [
    {
      id: 'register',
      label: 'Start my application',
      href: appendConversationToRegisterHref('/register', state.conversationId, courseCode),
      actionType: 'START_APPLICATION',
    },
    { id: 'human', label: 'Talk to a human', support: 'Connect me with SMA Admissions.' },
  ]
}

function beginNameCapture(state, userText, introLines) {
  if (state.answers.leadName && !state.answers.leadPhone) {
    return userThenAdvisor(state, userText, [PHONE_PROMPT, PRIVACY_COPY], {
      step: 'capture_phone',
      answers: { ...state.answers, otherQuestion: userText },
      options: [],
      inputMode: true,
      recommendation: state.recommendation,
      lead: { ...state.lead, offered: true },
    })
  }
  if (state.answers.leadName && state.answers.leadPhone) {
    return userThenAdvisor(state, userText, [
      `Thanks, ${firstName(state.answers.leadName)}. SMA Admissions can follow up using the details you already shared.`,
      PRIVACY_COPY,
    ], {
      step: 'followup',
      answers: { ...state.answers, otherQuestion: userText },
      options: registerOptions(state),
      inputMode: false,
      recommendation: state.recommendation,
      lead: { ...state.lead, offered: true, captured: true },
    })
  }
  return userThenAdvisor(state, userText, introLines, {
    step: 'capture_name',
    answers: { ...state.answers, otherQuestion: userText },
    options: [],
    inputMode: true,
    recommendation: state.recommendation,
    lead: { ...state.lead, offered: true },
  })
}

function factualReply(state, userText, advisorTexts, extras = {}) {
  syncLead(state, { message: userText, action: 'qualify' })
  return userThenAdvisor(state, userText, advisorTexts, {
    step: 'followup',
    answers: { ...state.answers, otherQuestion: userText },
    options: FOLLOW_UP,
    inputMode: false,
    recommendation: extras.recommendation === undefined ? state.recommendation : extras.recommendation,
    ...extras,
  })
}

function applyAiReply(state, userText, payload) {
  const course = payload?.recommendation?.courseCode
    ? byCode(payload.recommendation.courseCode)
    : null
  const actions = mapAdvisorActions(payload?.actions, state.conversationId)
  const options = actions.length ? actions : FOLLOW_UP
  const nextAnswers = {
    ...state.answers,
    otherQuestion: userText,
    recommendedCourseCode: course?.code || state.answers.recommendedCourseCode || null,
  }
  const nextState = {
    ...state,
    answers: nextAnswers,
    recommendation: course || state.recommendation,
    lead: payload?.lead ? { ...state.lead, status: payload.lead.status } : state.lead,
  }

  if (shouldFollowCaptureStep(nextState, payload, userText) || shouldOfferCapture(nextState, userText)) {
    const step = payload?.captureStep || 'ask_name'
    if (step === 'ask_phone' && nextState.answers.leadName) {
      return userThenAdvisor(nextState, userText, [payload.message, PHONE_PROMPT, PRIVACY_COPY], {
        step: 'capture_phone',
        answers: { ...nextState.answers, otherQuestion: userText },
        options: [],
        inputMode: true,
        recommendation: nextState.recommendation,
        lead: { ...nextState.lead, offered: true },
      })
    }
    return beginNameCapture(nextState, userText, [
      payload.message,
      NAME_PROMPT,
      PRIVACY_COPY,
    ])
  }

  const offeredApply = actions.some((action) => action.actionType === 'START_APPLICATION')
  return userThenAdvisor(nextState, userText, [payload.message], {
    step: course ? 'recommend' : 'ai',
    answers: {
      ...nextAnswers,
      applicationCtaOffered: nextState.answers.applicationCtaOffered || offeredApply,
    },
    options,
    inputMode: true,
    recommendation: course || state.recommendation,
    lead: nextState.lead,
  })
}

async function localConfirmedFallback(state, text) {
  const lower = text.toLowerCase()
  const targetCourse = state.targetCourse || (state.answers?.targetCourseCode ? byCode(state.answers.targetCourseCode) : null)

  if (targetCourse) {
    if (/(fee|cost|price|tuition|how much|pay|installment)/.test(lower)) {
      const fee = targetCourse.feeSource === 'postgresql' && targetCourse.fee ? targetCourse.fee : null
      return factualReply(state, text, [
        fee
          ? `The tuition fee for ${targetCourse.title} (${targetCourse.code}) is ${fee}.`
          : `I don't have a confirmed live fee for ${targetCourse.title} yet.`,
        `You can pay in installments: a 50% deposit before classes commence, and the remaining balance during the course.`,
      ], {
        options: createCourseFollowUpOptions(targetCourse),
      })
    }

    if (/(duration|how long|weeks|months|schedule|time|intake|when)/.test(lower)) {
      const duration = targetCourse.durationSource === 'postgresql' && targetCourse.duration
        ? targetCourse.duration
        : null
      return factualReply(state, text, [
        duration
          ? `${targetCourse.title} runs for ${duration}.`
          : `I don't have a confirmed live duration for ${targetCourse.title} yet.`,
      ], {
        options: createCourseFollowUpOptions(targetCourse),
      })
    }

    if (/(learn|syllabus|curriculum|module|teach|content|cover)/.test(lower)) {
      const lines = [
        `${targetCourse.title} is an intensive ${targetCourse.level.toLowerCase()} program in ${targetCourse.category}.`,
        targetCourse.description,
      ]
      if (targetCourse.advantages?.length) {
        lines.push(`Key highlights:\n• ${targetCourse.advantages.slice(0, 3).join('\n• ')}`)
      }
      return factualReply(state, text, lines, {
        options: createCourseFollowUpOptions(targetCourse),
      })
    }

    if (/(career|job|work|opportunity|role|employ|freelance)/.test(lower)) {
      const jobs = targetCourse.jobOpportunities?.length ? targetCourse.jobOpportunities.join(', ') : 'Commercial and freelance creator roles'
      return factualReply(state, text, [
        `Graduates of ${targetCourse.title} target roles including: ${jobs}.`,
        `You will build and defend a real-world portfolio before graduation.`,
      ], {
        options: createCourseFollowUpOptions(targetCourse),
      })
    }

    if (/(camera|equipment|software|tool|laptop|pc|mac|studio|gear)/.test(lower)) {
      return factualReply(state, text, [
        `All cameras, studio lighting, audio rigs, and workstation computers with licensed software/AI tools are provided at SMA during ${targetCourse.title}.`,
        `You do not need to bring your own expensive equipment to get started.`,
      ], {
        options: createCourseFollowUpOptions(targetCourse),
      })
    }
  }

  if (/slogan/.test(lower)) {
    return factualReply(state, text, [getOfficialSlogan() + '.'])
  }

  if (/(founding|founded|established|started|start year|what year)/.test(lower)) {
    return factualReply(state, text, getFoundingYearAdvisorLines())
  }

  if (/(office hours|opening hours|open hours|saturday|sunday|when are you open)/.test(lower)) {
    return factualReply(state, text, getHoursAdvisorLines())
  }

  if (/(website|web site|url|domain)/.test(lower)) {
    return factualReply(state, text, [getOfficialWebsite()])
  }

  if (/email|e-mail/.test(lower)) {
    return factualReply(state, text, [getOfficialEmail()])
  }

  if (/(can i (come )?(and )?visit|want to visit|come and visit|visit the academy|visit the campus)/i.test(lower)) {
    return factualReply(state, text, getVisitAdvisorLines(), {
      options: [{ id: 'contact', label: 'Talk to Admissions', href: '/contact', actionType: 'CONTACT_ADMISSIONS' }],
      inputMode: false,
    })
  }

  if (/(where are you|location|located|address|campus|kampala|kubiri)/.test(lower)) {
    return factualReply(state, text, getLocationAdvisorLines())
  }

  if (/(installment|instalment|pay in|payment plan|50%|50 percent|mobile money)/.test(lower)) {
    return factualReply(state, text, getPaymentAdvisorLines())
  }

  if (/(do you (give|offer|provide) (a )?certificate|get a certificate|receive a certificate)/.test(lower)) {
    return factualReply(state, text, getCertificateAdvisorLines())
  }

  if (
    /(academic qualification|prior experience|no experience required|do i need (any )?qualifications|what are the (admission )?requirements)/.test(lower)
    && !/(camera|equipment)/.test(lower)
  ) {
    return factualReply(state, text, getRequirementsAdvisorLines())
  }

  if (/(own camera|have a camera|bring.*camera|need (a |my own )?(camera|equipment)|own equipment)/.test(lower)) {
    return factualReply(state, text, getEquipmentAdvisorLines())
  }

  const toolLines = findToolAdvisorLines(text)
  if (toolLines) {
    return factualReply(state, text, toolLines)
  }

  if (/masterclass|next session|guest speaker/.test(lower)) {
    const session = await getUpcomingMasterclass()
    const when = formatMasterclassDate(session)
    if (when) {
      const topic = session.topic ? ` ${session.topic}` : ''
      return factualReply(state, text, [`The next SMA Masterclass is${topic ? ` “${topic}”` : ''} on ${when}.`])
    }
    return factualReply(state, text, getMasterclassAdvisorLines())
  }

  if (
    /(how long (are|do) (your|the|sma)|how long do your courses|duration of (your|the) courses)/i.test(text)
    && !state.answers.recommendedCourseCode
    && !/(fee|cost|price)/.test(lower)
  ) {
    return factualReply(state, text, getDurationAdvisorLines())
  }

  if (/(fee|cost|price|tuition|how much)/.test(lower)) {
    const course = findCourseForQuestion(text)
    if (course) {
      return factualReply(state, text, [getCourseFeeLine(course)])
    }
  }

  if (/(weekend|saturday|sunday)/.test(lower)) {
    return factualReply(state, text, [
      ...getHoursAdvisorLines(),
      ...getIntakeAdvisorLines().slice(0, 2),
    ])
  }

  return null
}

export async function submitOtherQuestion(state, rawText) {
  const text = rawText.trim()
  if (!text) return state

  if (state.step === 'capture_offer') {
    if (/^(yes|yeah|yep|sure|ok|okay|please|connect)/i.test(text)) {
      return beginNameCapture(state, text, [
        NAME_PROMPT,
        PRIVACY_COPY,
      ])
    }
    return userThenAdvisor(state, text, [CTA_DECLINE_COPY], {
      step: 'followup',
      options: FOLLOW_UP,
      inputMode: true,
      answers: { ...state.answers, applicationCtaDeclined: true, contactDeclined: true },
      lead: { ...state.lead, offered: true },
    })
  }

  if (state.step === 'capture_name') {
    if (isCtaDecline(text) || /don'?t (want to|wanna) (give|share|leave|provide)/i.test(text)) {
      return userThenAdvisor(state, text, [CTA_DECLINE_COPY], {
        step: 'followup',
        answers: { ...state.answers, contactDeclined: true },
        options: FOLLOW_UP,
        inputMode: true,
        lead: { ...state.lead, offered: true },
      })
    }
    const name = extractName(text, { allowBare: true })
    const phone = extractPhone(text)
    if (!name && !phone) {
      return userThenAdvisor(state, text, ["I didn't quite catch your name. " + NAME_PROMPT], {
        step: 'capture_name',
        options: [],
        inputMode: true,
      })
    }
    if (name && phone) {
      const lead = await syncLead(state, { name, phone, message: text, action: 'capture' })
      const interest = interestLabel(state.answers, text)
      return userThenAdvisor(state, text, [
        `Thanks, ${firstName(name)}. I've noted your interest${interest ? ` in ${interest}` : ''}. SMA Admissions can now follow up with you.`,
        PRIVACY_COPY,
      ], {
        step: 'followup',
        answers: { ...state.answers, leadName: name, leadPhone: phone },
        options: registerOptions(state),
        inputMode: false,
        lead: { ...state.lead, status: lead?.status || 'QUALIFIED', name, phone, captured: true, offered: true },
      })
    }
    return userThenAdvisor(state, text, [
      PHONE_PROMPT,
      PRIVACY_COPY,
    ], {
      step: 'capture_phone',
      answers: { ...state.answers, leadName: name },
      options: [],
      inputMode: true,
      lead: { ...state.lead, name, offered: true },
    })
  }

  if (state.step === 'capture_phone') {
    if (isCtaDecline(text) || /don'?t (want to|wanna) (give|share|leave|provide)/i.test(text)) {
      return userThenAdvisor(state, text, [CTA_DECLINE_COPY], {
        step: 'followup',
        answers: { ...state.answers, contactDeclined: true },
        options: FOLLOW_UP,
        inputMode: true,
        lead: { ...state.lead, offered: true },
      })
    }
    const phone = extractPhone(text)
    if (!phone) {
      return userThenAdvisor(state, text, ['Please share a WhatsApp or phone number SMA Admissions can use, for example 07xx xxx xxx.'], {
        step: 'capture_phone',
        options: [],
        inputMode: true,
      })
    }
    const name = state.answers.leadName
    const lead = await syncLead(state, { name, phone, message: text, action: 'capture' })
    const interest = interestLabel(state.answers, text)
    return userThenAdvisor(state, text, [
      `Thanks, ${firstName(name)}. I've noted your interest${interest ? ` in ${interest}` : ''}. SMA Admissions can now follow up with you.`,
    ], {
      step: 'followup',
      answers: { ...state.answers, leadPhone: phone },
      options: registerOptions(state),
      inputMode: false,
      lead: { ...state.lead, status: lead?.status || 'QUALIFIED', phone, captured: true, offered: true },
    })
  }

  if (state.step === 'human_lead') {
    return submitOtherQuestion({ ...state, step: 'capture_name' }, text)
  }

  const volunteeredName = extractName(text)
  const volunteeredPhone = extractPhone(text)
  if ((volunteeredName || volunteeredPhone) && (isContactIntent(text) || (volunteeredName && volunteeredPhone))) {
    const name = volunteeredName || state.answers.leadName
    const phone = volunteeredPhone || state.answers.leadPhone
    if (name && phone) {
      const lead = await syncLead(state, { name, phone, message: text, action: 'capture' })
      const nextState = {
        ...state,
        answers: { ...state.answers, leadName: name, leadPhone: phone },
        lead: { ...state.lead, status: lead?.status || 'QUALIFIED', name, phone, captured: true, offered: true },
      }
      if (!/(want|study|learn|course|photograph|design|video|animat|register|apply)/i.test(text)) {
        const interest = interestLabel(nextState.answers, text)
        return userThenAdvisor(nextState, text, [
          `Thanks, ${firstName(name)}. I've noted your interest${interest ? ` in ${interest}` : ''}. SMA Admissions can now follow up with you.`,
          PRIVACY_COPY,
        ], {
          step: 'followup',
          answers: nextState.answers,
          options: registerOptions(nextState),
          inputMode: false,
          lead: nextState.lead,
        })
      }
      state = nextState
    } else if (isContactIntent(text)) {
      return beginNameCapture({
        ...state,
        answers: { ...state.answers, leadName: name || state.answers.leadName, leadPhone: phone || state.answers.leadPhone },
      }, text, [NAME_PROMPT, PRIVACY_COPY])
    }
  }

  const lower = text.toLowerCase()

  if (isCtaDecline(text) && state.answers.applicationCtaOffered) {
    return userThenAdvisor(state, text, [CTA_DECLINE_COPY], {
      step: 'followup',
      answers: { ...state.answers, applicationCtaDeclined: true, contactDeclined: true, otherQuestion: text },
      options: FOLLOW_UP,
      inputMode: true,
    })
  }

  if (isContactIntent(text)) {
    syncLead(state, { message: text, action: 'qualify' })
    return beginNameCapture(state, text, [NAME_PROMPT, PRIVACY_COPY])
  }

  if (isHumanIntent(text)) {
    syncLead(state, { message: text, action: 'qualify' })
    return userThenAdvisor(state, text, [HUMAN_SAFE_COPY, ...getHumanAdvisorLines().filter((line) => line !== HUMAN_SAFE_COPY)], {
      step: 'human',
      answers: { ...state.answers, otherQuestion: text },
      options: [
        { id: 'contact', label: 'Talk to Admissions', href: '/contact', actionType: 'CONTACT_ADMISSIONS' },
        { id: 'leave-details', label: 'Leave my details', support: 'Share a name and number for Admissions.' },
      ],
      inputMode: false,
    })
  }

  if (/(accommodation|hostel|housing|dormitor|scholarship|employment guarantee|job guarantee|internship guarantee)/i.test(lower)) {
    return factualReply(state, text, getUnknownAdvisorLines(), {
      options: [{ id: 'contact', label: 'Talk to Admissions', href: '/contact' }],
      inputMode: false,
    })
  }

  try {
    const payload = await askAdvisorAi({
      message: text,
      history: state.messages,
      answers: chatAnswersFromState(state),
      conversationId: state.conversationId,
      messageId: crypto.randomUUID(),
    })
    if (payload?.message && !payload.fallback) {
      return applyAiReply(state, text, payload)
    }
    if (payload?.fallback && payload.message) {
      if (shouldFollowCaptureStep(state, payload, text)) {
        return applyAiReply(state, text, payload)
      }
      return factualReply(state, text, [payload.message], {
        options: mapAdvisorActions(payload.actions, state.conversationId).length
          ? mapAdvisorActions(payload.actions, state.conversationId)
          : [{ id: 'contact', label: 'Talk to Admissions', href: '/contact' }],
        inputMode: false,
        lead: payload.lead ? { ...state.lead, status: payload.lead.status } : state.lead,
      })
    }
  } catch {
    // Use trusted local knowledge when the AI service is unavailable.
  }

  const local = await localConfirmedFallback(state, text)
  if (local) return local

  if (/(ready to (register|apply|enroll)|want to (register|apply|enroll|join)|how do i (apply|register|enroll|join)|how can i (apply|register|enroll|join)|where do i (apply|register)|start (my )?application|send me (the )?(registration|application) link|i want to sign up)/i.test(text)) {
    syncLead(state, { message: text, action: 'qualify' })
    const course = state.recommendation
    const title = course?.title || 'this course'
    const fee = course?.feeSource === 'postgresql' && course?.fee ? course.fee : null
    const duration = course?.durationSource === 'postgresql' && course?.duration ? course.duration : null
    const detail = [fee, duration].filter(Boolean).join(' and runs for ')
    const answer = detail
      ? `${title} is ${detail}. Since you're ready to join, you can register here.`
      : `Since you're ready to join${course?.title ? ` ${course.title}` : ''}, you can register here.`
    return userThenAdvisor(
      state,
      text,
      [answer],
      {
        step: 'followup',
        answers: { ...state.answers, otherQuestion: text, applicationCtaOffered: true },
        options: registerOptions(state),
        inputMode: false,
        recommendation: state.recommendation,
      }
    )
  }

  if (/(not sure|unsure|what course to choose|which course should|help me (choose|pick|decide))/i.test(lower)) {
    return factualReply(state, text, [
      'Absolutely — I can help you figure that out. What would you most like to create: photos, videos, designs, animations, or digital content?',
    ])
  }

  if (/(intake|schedule|when|time|start|weekend|saturday|sunday|evening class)/.test(lower)) return applyTopic(state, 'intakes', text)
  if (/(equipment|studio|camera|facilit)/.test(lower)) return applyTopic(state, 'facilities', text)
  if (/(career|job|work|freelance)/.test(lower)) return applyTopic(state, 'careers', text)
  if (/(learn|curriculum|skill)/.test(lower)) return applyTopic(state, 'learn', text)
  if (/(course|program|which|recommend)/.test(lower)) return applyTopic(state, 'discover', text)

  return factualReply(state, text, [
    "I'm having a little trouble connecting right now. You can still contact SMA Admissions directly and we'll be happy to help.",
  ], {
    options: [{ id: 'contact', label: 'Talk to Admissions', href: '/contact' }],
  })
}

function applyTopic(state, topicId, overrideUserText) {
  const topic = topicById(topicId)
  const userText = overrideUserText || topic?.userText || topic?.label

  if (topicId === 'discover') {
    return userThenAdvisor(
      state,
      userText,
      ['What would you like to create?'],
      {
        step: 'discover_interest',
        answers: { ...state.answers, topic: topicId, recommendedCourseCode: null },
        options: INTERESTS,
        inputMode: false,
        recommendation: null,
      }
    )
  }

  if (topicId === 'fees') {
    const invite = withCaptureInvite(
      state,
      ['Here are SMA’s current program areas.', 'Choose one and I’ll show the official duration and fee.'],
      AREAS
    )
    return userThenAdvisor(state, userText, invite.texts, {
      step: 'fees_area',
      answers: { ...state.answers, topic: topicId },
      options: invite.options,
      inputMode: false,
      recommendation: null,
    })
  }

  if (topicId === 'intakes') {
    const invite = withCaptureInvite(state, getIntakeAdvisorLines(), FOLLOW_UP)
    return userThenAdvisor(state, userText, invite.texts, {
      step: 'followup',
      answers: { ...state.answers, topic: topicId },
      options: invite.options,
      inputMode: false,
      recommendation: null,
    })
  }

  if (topicId === 'learn') {
    const invite = withCaptureInvite(
      state,
      ['That depends on the path you choose.', 'Which area are you most curious about?'],
      AREAS
    )
    return userThenAdvisor(state, userText, invite.texts, {
      step: 'learn_area',
      answers: { ...state.answers, topic: topicId },
      options: invite.options,
      inputMode: false,
      recommendation: null,
    })
  }

  if (topicId === 'masterclass') {
    const invite = withCaptureInvite(state, getMasterclassAdvisorLines(), [
      { id: 'masterclass-page', label: 'Explore Masterclass', href: '/masterclass' },
      { id: 'human', label: 'Talk to a human', support: 'Connect me with SMA Admissions.' },
      { id: 'discover', label: 'Find the right course', support: 'Help me choose the best path.' },
    ])
    return userThenAdvisor(state, userText, invite.texts, {
      step: 'followup',
      answers: { ...state.answers, topic: topicId },
      options: invite.options,
      inputMode: false,
      recommendation: null,
    })
  }

  if (topicId === 'facilities') {
    const invite = withCaptureInvite(state, getFacilitiesAdvisorLines(), FOLLOW_UP)
    return userThenAdvisor(state, userText, invite.texts, {
      step: 'followup',
      answers: { ...state.answers, topic: topicId },
      options: invite.options,
      inputMode: false,
      recommendation: null,
    })
  }

  if (topicId === 'careers') {
    return userThenAdvisor(
      state,
      userText,
      ['SMA trains for real creative work — freelance, studio, and brand roles.', 'Which field should I show you?'],
      {
        step: 'careers_area',
        answers: { ...state.answers, topic: topicId },
        options: AREAS,
        inputMode: false,
        recommendation: null,
      }
    )
  }

  if (topicId === 'human') {
    syncLead(state, { message: userText, action: 'qualify' })
    if (state.answers?.contactDeclined) {
      return userThenAdvisor(state, userText, [...getHumanAdvisorLines(), CTA_DECLINE_COPY], {
        step: 'human',
        answers: { ...state.answers, topic: topicId },
        options: [
          { id: 'contact', label: 'Go to contact', href: '/contact', actionType: 'CONTACT_ADMISSIONS' },
          { id: 'register', label: 'Start my application', href: appendConversationToRegisterHref('/register', state.conversationId, state.answers.recommendedCourseCode || state.recommendation?.code), actionType: 'START_APPLICATION' },
          { id: 'discover', label: 'Find the right course', support: 'Help me choose the best path.' },
        ],
        inputMode: true,
        recommendation: state.recommendation,
      })
    }
    return beginNameCapture(state, userText, [
      ...getHumanAdvisorLines(),
      NAME_PROMPT,
      PRIVACY_COPY,
    ])
  }

  if (topicId === 'other') {
    return userThenAdvisor(
      state,
      userText,
      ['Of course. Type your question below.'],
      {
        step: 'other',
        answers: { ...state.answers, topic: topicId },
        options: [],
        inputMode: true,
        recommendation: null,
      }
    )
  }

  return state
}

function applyAreaList(state, areaId, label, stepAfter) {
  const list = areaId === 'unsure'
    ? [byCode('PHOTO-101'), byCode('GRAPH-GD-001'), byCode('AI-TOOLS-001'), byCode('MASTER-MULTI-001')].filter(Boolean)
    : coursesInArea(areaId)

  if (stepAfter === 'fees_list') {
    const lines = list.map((course) => getCourseFeeLine(course))
    const invite = withCaptureInvite(
      state,
      ['These are the official SMA programs in that area.', ...lines.slice(0, 6)],
      [
        ...list.slice(0, 4).map((course) => ({ id: `course:${course.code}`, label: course.title })),
        ...FOLLOW_UP,
      ]
    )
    return userThenAdvisor(state, label, invite.texts, {
      step: 'followup',
      options: invite.options,
      inputMode: false,
      recommendation: null,
    })
  }

  if (stepAfter === 'learn_detail') {
    const course = list[0]
    const outcomes = (course?.whatYouCanDo || []).slice(0, 3)
    const invite = withCaptureInvite(
      state,
      [
        course ? `In ${course.title}, students learn to:` : 'Here is what students typically work toward:',
        ...outcomes,
      ],
      [
        ...(course ? [{ id: `course:${course.code}`, label: `See ${course.title}` }] : []),
        ...FOLLOW_UP,
      ]
    )
    return userThenAdvisor(state, label, invite.texts, {
      step: 'followup',
      options: invite.options,
      inputMode: false,
      recommendation: null,
    })
  }

  const jobs = list.flatMap((course) => course.jobOpportunities).slice(0, 4)
  const invite = withCaptureInvite(
    state,
    ['Graduates from this path often move into roles like these:', ...jobs],
    FOLLOW_UP
  )
  return userThenAdvisor(state, label, invite.texts, {
    step: 'followup',
    options: invite.options,
    inputMode: false,
    recommendation: null,
  })
}

function overlayLiveRecommendation(local, rec) {
  if (!rec?.courseCode) return null
  return {
    ...(local || {}),
    code: rec.courseCode,
    title: rec.title || local?.title || rec.courseCode,
    name: rec.title || local?.name || rec.courseCode,
    fee: rec.fee || null,
    duration: rec.duration || null,
    feeSource: rec.fee ? 'postgresql' : 'unavailable',
    durationSource: rec.duration ? 'postgresql' : 'unavailable',
    category: rec.category || local?.category || '',
    level: rec.level || local?.level || '',
    whatYouCanDo: local?.whatYouCanDo || [],
  }
}

function clarificationOptionsFor(question) {
  if (/camera|edited|filmed|design tools|scratch|experience|basics|animation/i.test(String(question || ''))) {
    return EXPERIENCE
  }
  return INTERESTS
}

function quizFallbackState(state, userText, answers) {
  return userThenAdvisor(state, userText, [
    "I'm having a little trouble connecting right now. You can still contact SMA Admissions directly and we'll be happy to help.",
  ], {
    step: 'followup',
    answers,
    options: [{ id: 'contact', label: 'Talk to Admissions', href: '/contact' }],
    inputMode: true,
    recommendation: null,
  })
}

async function applyQuizRecommendation(state, userText, answers) {
  try {
    const payload = await requestCourseRecommendation({
      answers,
      conversationId: state.conversationId,
    })

    if (payload?.fallback && !payload?.recommendation) {
      return quizFallbackState(state, userText, answers)
    }

    if (payload?.needsClarification || !payload?.recommendation?.courseCode) {
      const question = payload?.clarificationQuestion
        || 'What would you most like to create — photos, videos, designs, animations, or digital content?'
      return userThenAdvisor(state, userText, [question], {
        step: 'discover_clarify',
        answers,
        options: clarificationOptionsFor(question),
        inputMode: false,
        recommendation: null,
      })
    }

    const course = overlayLiveRecommendation(byCode(payload.recommendation.courseCode), payload.recommendation)
    const canOfferCapture = !answers.leadName || !answers.leadPhone
    const declined = Boolean(state.answers?.contactDeclined || answers.contactDeclined)
    return userThenAdvisor(state, userText, [
      "Based on what you've told me...",
      !declined && canOfferCapture
        ? 'Would you like me to save these details and have SMA Admissions follow up with you?'
        : null,
    ].filter(Boolean), {
      step: 'recommend',
      answers: {
        ...answers,
        recommendedCourseCode: payload.recommendation.courseCode,
        recommendationReason: payload.reason || null,
        recommendationNextStep: payload.nextStep || null,
      },
      recommendation: course,
      options: !declined && canOfferCapture
        ? [
          { id: 'lead-yes', label: 'Yes, save my details', support: 'SMA Admissions can follow up.' },
          { id: 'lead-later', label: 'Not right now' },
        ]
        : [],
      inputMode: false,
    })
  } catch {
    return quizFallbackState(state, userText, answers)
  }
}

export async function completeDiscoverRecommendation(state, option) {
  if (!option) return state
  const answers = {
    ...state.answers,
    experience: option.id,
    recommendedCourseCode: null,
  }
  return applyQuizRecommendation(state, option.label, answers)
}

export async function completeDiscoverClarification(state, option) {
  if (!option) return state
  const isExperience = EXPERIENCE.some((item) => item.id === option.id)
  const answers = {
    ...state.answers,
    recommendedCourseCode: null,
    ...(isExperience ? { experience: option.id } : { interest: option.id }),
  }
  return applyQuizRecommendation(state, option.label, answers)
}

export function applyChoice(state, option) {
  if (!option) return state

  if (option.id === 'restart') return startAgain(state)

  if (option.id === 'lead-yes') {
    return beginNameCapture(state, option.label, [
      NAME_PROMPT,
      PRIVACY_COPY,
    ])
  }

  if (option.id === 'lead-later') {
    return userThenAdvisor(state, option.label, [CTA_DECLINE_COPY], {
      step: 'followup',
      options: FOLLOW_UP,
      inputMode: true,
      answers: { ...state.answers, applicationCtaDeclined: true, contactDeclined: true },
      lead: { ...state.lead, offered: true },
    })
  }

  if (option.id === 'leave-details') {
    return beginNameCapture(state, option.label, [
      NAME_PROMPT,
      PRIVACY_COPY,
    ])
  }

  if (option.href) {
    return state
  }

  if (String(option.id).startsWith('course:')) {
    const code = option.id.split(':')[1]
    const course = byCode(code)
    if (!course) return state
    return userThenAdvisor(
      state,
      option.label,
      ["Based on what you've told me..."],
      {
        step: 'recommend',
        recommendation: course,
        options: [],
        inputMode: false,
      }
    )
  }

  if (String(option.id).startsWith('course_learn:')) {
    const code = option.id.split(':')[1]
    const course = byCode(code) || state.targetCourse || state.recommendation
    if (!course) return state
    const lines = [
      `${course.title} (${course.code}) is an intensive ${course.level.toLowerCase()} program in ${course.category}.`,
      course.description,
    ]
    if (course.advantages && course.advantages.length > 0) {
      lines.push(`Studio & Practical Advantages:\n• ${course.advantages.slice(0, 4).join('\n• ')}`)
    }
    if (course.whatYouCanDo && course.whatYouCanDo.length > 0) {
      lines.push(`What you will build & produce:\n• ${course.whatYouCanDo.slice(0, 3).join('\n• ')}`)
    }
    return userThenAdvisor(
      state,
      option.userText || option.label,
      lines,
      {
        step: 'course_inquiry',
        targetCourse: course,
        recommendation: course,
        answers: { ...state.answers, targetCourseCode: course.code, recommendedCourseCode: course.code },
        options: createCourseFollowUpOptions(course),
        inputMode: true,
      }
    )
  }

  if (String(option.id).startsWith('course_fee:')) {
    const code = option.id.split(':')[1]
    const course = byCode(code) || state.targetCourse || state.recommendation
    if (!course) return state
    const fee = course.feeSource === 'postgresql' && course.fee ? course.fee : null
    const lines = [
      fee
        ? `The tuition fee for ${course.title} is ${fee}.`
        : `I don't have a confirmed live fee for ${course.title} yet.`,
      `Course fees can be paid in installments, with 50% paid before starting and the remaining balance paid during the course.`,
    ]
    return userThenAdvisor(
      state,
      option.userText || option.label,
      lines,
      {
        step: 'course_inquiry',
        targetCourse: course,
        recommendation: course,
        answers: { ...state.answers, targetCourseCode: course.code, recommendedCourseCode: course.code },
        options: createCourseFollowUpOptions(course),
        inputMode: true,
      }
    )
  }

  if (String(option.id).startsWith('course_duration:')) {
    const code = option.id.split(':')[1]
    const course = byCode(code) || state.targetCourse || state.recommendation
    if (!course) return state
    const duration = course.durationSource === 'postgresql' && course.duration ? course.duration : null
    const lines = [
      duration
        ? `${course.title} runs for ${duration}.`
        : `I don't have a confirmed live duration for ${course.title} yet.`,
    ]
    return userThenAdvisor(
      state,
      option.userText || option.label,
      lines,
      {
        step: 'course_inquiry',
        targetCourse: course,
        recommendation: course,
        answers: { ...state.answers, targetCourseCode: course.code, recommendedCourseCode: course.code },
        options: createCourseFollowUpOptions(course),
        inputMode: true,
      }
    )
  }

  if (String(option.id).startsWith('course_careers:')) {
    const code = option.id.split(':')[1]
    const course = byCode(code) || state.targetCourse || state.recommendation
    if (!course) return state
    const jobs = course.jobOpportunities && course.jobOpportunities.length > 0
      ? course.jobOpportunities.join(', ')
      : 'Commercial studio, agency, and freelance creator roles'
    const lines = [
      `Graduates of ${course.title} are equipped for high-demand roles including: ${jobs}.`,
      `Throughout the course, you will build and defend a professional client-ready portfolio before graduation to showcase directly to employers and clients.`,
    ]
    return userThenAdvisor(
      state,
      option.userText || option.label,
      lines,
      {
        step: 'course_inquiry',
        targetCourse: course,
        recommendation: course,
        answers: { ...state.answers, targetCourseCode: course.code, recommendedCourseCode: course.code },
        options: createCourseFollowUpOptions(course),
        inputMode: true,
      }
    )
  }

  if (String(option.id).startsWith('course_equipment:')) {
    const code = option.id.split(':')[1]
    const course = byCode(code) || state.targetCourse || state.recommendation
    if (!course) return state
    const lines = [
      `At SMA, you will have hands-on access to professional industry equipment (DSLR and mirrorless cameras, cinema lighting, audio rigs, and dedicated workstations with licensed Adobe/AI creative software).`,
      `You do not need to own high-end equipment to enroll — all core studio gear and software are provided at our Makerere Kubiri campus.`,
    ]
    return userThenAdvisor(
      state,
      option.userText || option.label,
      lines,
      {
        step: 'course_inquiry',
        targetCourse: course,
        recommendation: course,
        answers: { ...state.answers, targetCourseCode: course.code, recommendedCourseCode: course.code },
        options: createCourseFollowUpOptions(course),
        inputMode: true,
      }
    )
  }

  if (option.id === 'discover') {
    return applyTopic(state, 'discover', option.userText || option.label)
  }

  if (option.id === 'human') {
    return applyTopic(state, 'human', option.userText || option.label)
  }

  if (state.step === 'course_inquiry') {
    return applyTopic(state, option.id, option.label)
  }

  if (state.step === 'topic' || state.step === 'followup' || state.step === 'human') {
    return applyTopic(state, option.id, option.id === topicById(option.id)?.id ? undefined : option.label)
  }

  if (state.step === 'discover_interest') {
    return userThenAdvisor(
      state,
      option.label,
      ['What are you hoping to achieve?'],
      {
        step: 'discover_goal',
        answers: { ...state.answers, interest: option.id },
        options: GOALS,
        inputMode: false,
      }
    )
  }

  if (state.step === 'discover_goal') {
    return userThenAdvisor(
      state,
      option.label,
      ["What's your current experience?"],
      {
        step: 'discover_experience',
        answers: { ...state.answers, goal: option.id },
        options: EXPERIENCE,
        inputMode: false,
      }
    )
  }

  if (state.step === 'discover_experience' || state.step === 'discover_clarify') {
    return state
  }

  if (state.step === 'fees_area') {
    return applyAreaList(state, option.id, option.label, 'fees_list')
  }

  if (state.step === 'learn_area') {
    return applyAreaList(state, option.id, option.label, 'learn_detail')
  }

  if (state.step === 'careers_area') {
    return applyAreaList(state, option.id, option.label, 'careers_detail')
  }

  return applyTopic(state, option.id)
}

export { getCourses as courses }
